import React from "react";

/**
 * Standalone stand-ins for the New Relic SDK's presentational components.
 *
 * Each one keeps the SDK's prop contract (including the enum statics that
 * consumers reference) but renders plain DOM styled by nr1-compat.css, so the
 * nerdlet's own styles.scss still drives the look.
 */

const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ------------------------------------------------------------------ layout */

export function Grid({
  children,
  className,
  // SDK-only props: consumed here so they never reach the DOM.
  spacingType,
  gapType,
  fullHeight,
  fullWidth,
  collapseGapAfter,
  ...rest
}) {
  return (
    <div className={cx("nr1c-grid", className)} {...rest}>
      {children}
    </div>
  );
}

Grid.SPACING_TYPE = {
  NONE: "none",
  EXTRA_SMALL: "extraSmall",
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
};
Grid.GAP_TYPE = Grid.SPACING_TYPE;

export function GridItem({
  children,
  className,
  columnSpan = 12,
  columnStart,
  columnEnd,
  style,
  spacingType,
  fullHeight,
  fullWidth,
  ...rest
}) {
  const span = Math.min(12, Math.max(1, Number(columnSpan) || 12));
  const gridColumn = columnStart
    ? `${columnStart} / span ${span}`
    : columnEnd
    ? `span ${span} / ${columnEnd}`
    : `span ${span}`;

  return (
    <div
      className={cx("nr1c-grid-item", className)}
      style={{ gridColumn, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

GridItem.SPACING_TYPE = Grid.SPACING_TYPE;

export function Stack({ children, className, directionType, ...rest }) {
  const vertical = directionType === Stack.DIRECTION_TYPE?.VERTICAL;
  return (
    <div
      className={cx("nr1c-stack", vertical && "nr1c-stack--vertical", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
Stack.DIRECTION_TYPE = { HORIZONTAL: "horizontal", VERTICAL: "vertical" };

export function StackItem({ children, className, grow, ...rest }) {
  return (
    <div
      className={cx("nr1c-stack-item", className)}
      style={grow ? { flexGrow: 1 } : undefined}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Card({ children, className, ...rest }) {
  return (
    <div className={cx("nr1c-card", className)} {...rest}>
      {children}
    </div>
  );
}

export function CardHeader({ children, title, className, ...rest }) {
  return (
    <div className={cx("nr1c-card-header", className)} {...rest}>
      {title ? <strong>{title}</strong> : null}
      {children}
    </div>
  );
}

export function CardBody({ children, className, ...rest }) {
  return (
    <div className={cx("nr1c-card-body", className)} {...rest}>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------- typography */

const HEADING_TAGS = {
  HEADING_1: "h1",
  HEADING_2: "h2",
  HEADING_3: "h3",
  HEADING_4: "h4",
  HEADING_5: "h5",
  HEADING_6: "h6",
};

export function HeadingText({
  children,
  className,
  type = "HEADING_1",
  tagType,
  spacingType,
  ...rest
}) {
  const key = String(tagType || type).toUpperCase();
  const Tag = HEADING_TAGS[key] || "h1";
  return (
    <Tag className={cx("nr1c-heading", className)} {...rest}>
      {children}
    </Tag>
  );
}

HeadingText.TYPE = Object.keys(HEADING_TAGS).reduce(
  (acc, key) => ({ ...acc, [key]: key }),
  {}
);

export function BlockText({ children, className, type, spacingType, tagType, ...rest }) {
  const Tag = String(type).toUpperCase() === "PARAGRAPH" ? "p" : "div";
  return (
    <Tag className={cx("nr1c-block-text", className)} {...rest}>
      {children}
    </Tag>
  );
}

BlockText.TYPE = { NORMAL: "NORMAL", PARAGRAPH: "PARAGRAPH" };

/* ---------------------------------------------------------------- controls */

export function Button({
  children,
  className,
  onClick,
  disabled,
  // SDK-only props. Note `type` is the SDK's visual variant, NOT the HTML
  // button type, so it must never be forwarded to the DOM node.
  type,
  sizeType,
  spacingType,
  iconType,
  loading,
  to,
  ...rest
}) {
  const variant = String(type || "NORMAL").toLowerCase();
  return (
    <button
      type="button"
      className={cx("nr1c-button", `nr1c-button--${variant}`, className)}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      {children}
    </button>
  );
}

Button.TYPE = {
  NORMAL: "NORMAL",
  PRIMARY: "PRIMARY",
  DESTRUCTIVE: "DESTRUCTIVE",
  PLAIN: "PLAIN",
  OUTLINE: "OUTLINE",
};
Button.SIZE_TYPE = { SMALL: "SMALL", MEDIUM: "MEDIUM", LARGE: "LARGE" };

export function Spinner({ className, inline, type, spacingType, ...rest }) {
  return (
    <span
      className={cx("nr1c-spinner", inline && "nr1c-spinner--inline", className)}
      role="status"
      aria-label="Loading"
      {...rest}
    />
  );
}

Spinner.TYPE = { DOT: "DOT", CIRCLE: "CIRCLE" };

/* ------------------------------------------------------------------- modal */

export function Modal({ children, hidden = false, onClose, className, ...rest }) {
  if (hidden) return null;
  return (
    <div className="nr1c-modal-overlay" onClick={onClose} role="presentation">
      <div
        className={cx("nr1c-modal", className)}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        {...rest}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({ title, children, className, ...rest }) {
  return (
    <div className={cx("nr1c-modal-header", className)} {...rest}>
      {title ? <h3>{title}</h3> : null}
      {children}
    </div>
  );
}

export function ModalBody({ children, className, ...rest }) {
  return (
    <div className={cx("nr1c-modal-body", className)} {...rest}>
      {children}
    </div>
  );
}

export function ModalFooter({ children, className, ...rest }) {
  return (
    <div className={cx("nr1c-modal-footer", className)} {...rest}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------ misc widgets */

export function Tooltip({ children, text, className, ...rest }) {
  return (
    <span className={cx("nr1c-tooltip", className)} title={text} {...rest}>
      {children}
    </span>
  );
}

export function Icon({ className, type, ...rest }) {
  return <span className={cx("nr1c-icon", className)} data-icon={type} {...rest} />;
}
Icon.TYPE = {};

export function EmptyState({ title, description, className, ...rest }) {
  return (
    <div className={cx("nr1c-empty-state", className)} {...rest}>
      {title ? <strong>{title}</strong> : null}
      {description ? <p>{description}</p> : null}
    </div>
  );
}
